/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Button, Card } from "core/components";
import { useFormContext, useFieldArray } from "react-hook-form";
import { TaskFormData, ChangeOrderTaskItemFormData } from "core/schema";
import ChangeOrderTaskItemForm from "./ChangeOrderTaskItemForm";
import ChangeOrderTaskItemsContext from "./ChangeOrderTaskItemsContext";

type ChangeOrderTaskItemsFormProps = {
  tasks: Array<TaskFormData>;
};

const ChangeOrderTaskItemsForm = ({ parentIndex, task, items, changeOrder, isItemsLoading }: ChangeOrderTaskItemsFormProps): JSX.Element => {
  const fieldArrayName = `change_order_tasks_attributes[${parentIndex}].change_order_task_items_attributes`;

  const {
    control,
    formState: { errors }
  } = useFormContext();
  const { fields, append, remove } = useFieldArray<ChangeOrderTaskItemFormData>({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [selectedItems, setSelectedItems] = React.useState<Array<TaskFormData>>([]);
  const [itemsForTask, setItemsForTask] = React.useState<Array<Record<string, unknown>>>(items);

  React.useEffect(() => {
    if (!isItemsLoading) {
      const itemsForTask =
        task?.name == "Particulars" || task?.name == "Change Order"
          ? items
          : items.filter((item) => task?.item_subcategories.includes(item.item_subcategory_id));

      setItemsForTask(itemsForTask);
    }
  }, [fields, task, isItemsLoading]);

  const canAppendItem = () => {
    return itemsForTask.length > fields.length;
  };

  return (
    <ChangeOrderTaskItemsContext.Provider value={{ selectedItems, setSelectedItems }}>
      <Card>
        <Card.Header title="Task Items" titleProps={{ className: "h5" }}>
          {errors.change_order_tasks_attributes?.[parentIndex]?.change_order_task_items_attributes?.length > 0 && (
            <Button className="button--danger mr-2" icon="exclamation-triangle" />
          )}
        </Card.Header>
        <Card.Body>
          {fields.map((item, index) => {
            return (
              <ChangeOrderTaskItemForm
                key={item.key}
                parentIndex={parentIndex}
                index={index}
                remove={remove}
                append={append}
                items={itemsForTask}
                changeOrder={changeOrder}
                isItemsLoading={isItemsLoading}
              />
            );
          })}
          {canAppendItem() && (
            <Button
              className="button--brand mt-4"
              title="Add Task Item"
              icon="plus"
              type="button"
              onClick={() => {
                append({ budgeted_quantity: null });
              }}
            />
          )}
        </Card.Body>
      </Card>
    </ChangeOrderTaskItemsContext.Provider>
  );
};

export default ChangeOrderTaskItemsForm;
